/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * PWA lifecycle helpers — service worker registration, update notification,
 * install prompt handling and iOS standalone detection.
 *
 * This file is loaded in all modes. registerServiceWorker() registers the SW
 * in all modes too; only the periodic update() polling is gated to production
 * (in dev, webpack-dev-server rebuilds the SW on every change — see the
 * NODE_ENV guard inside registerServiceWorker).
 */

import {useEffect, useState} from 'react';

import {Workbox} from 'workbox-window';

import i18n from '../i18n';

/** Fire a DOM CustomEvent on window so React components can listen via addEventListener. */
function dispatchWindowEvent(name: string): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(name));
}

/** BeforeInstallPromptEvent is not yet in the lib DOM types. */
interface BeforeInstallPromptEvent extends Event {
    readonly platforms: ReadonlyArray<string>;
    readonly userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string}>;
    prompt(): Promise<void>;
}

// Module-scope state — a SW registration / install prompt are process-global.
let workbox: Workbox | null = null;
let registration: ServiceWorkerRegistration | undefined;
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
// Update-available lifecycle (notify + apply — silent auto-reload).

// Set by applyServiceWorkerUpdate() once WE have asked the waiting worker to
// activate. 'controlling' also fires on the very first install (clients.claim()
// on activate) and in OTHER tabs when a different tab triggered the update —
// reloading there would kick the user out of their work — so the 'controlling'
// listener reloads only when this flag says the activation was ours.
let swUpdateActivating = false;
let deferredReloadArmed = false;

// sessionStorage flag set right before an update reload, read once on the next
// load to show the "app updated" toast. sessionStorage (not localStorage) keeps
// it scoped to this tab's reload — it never leaks to other tabs/sessions.
export const PWA_JUST_UPDATED_KEY = 'bolmate-pwa-just-updated';

/** True when the user is mid-edit — used to DEFER the auto-update reload so
 *  unsaved input is never discarded. Covers native input/textarea/select,
 *  contenteditable regions, and focus INSIDE a child iframe: the rich-text
 *  email / PDF-template editors (TinyMCE in IFRAME mode, Unlayer) render their
 *  editable area in an iframe, so when focus is there this document's
 *  activeElement is the <iframe> element itself (tagName IFRAME, its own
 *  isContentEditable false). Treating a focused iframe as editing is the safe
 *  default — we would rather delay an update than discard unsaved work. */
export function isUserEditingInput(): boolean {
    if (typeof document === 'undefined') return false;
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return tag === 'IFRAME';
}

/**
 * Custom event dispatched when beforeinstallprompt fires (Android/Chrome-desktop)
 * or when the iOS install hint should be re-evaluated after a dismissal.
 */
export const PWA_INSTALL_AVAILABLE_EVENT = 'pwa:install-available';

const INSTALL_HINT_DISMISSED_KEY = 'bolmate-pwa-install-dismissed';
const SESSION_COUNT_KEY = 'bolmate-pwa-session-count';

// Backend user.flex "installed" heartbeat: the epoch-ms timestamp of the last
// time the user launched the installed PWA (standalone). Unlike localStorage —
// which iOS isolates between Safari and the home-screen app — user.flex
// round-trips through the backend and is shared by every context the same user
// logs into, so Safari can read it to hide the iOS install hint after the user
// has actually installed + opened the app.
//
// Why a timestamp and not a boolean: iOS fires NO uninstall event and Safari
// cannot observe whether the home-screen app still exists. The previous
// write-once boolean therefore stuck forever — after uninstalling, the install
// hint never came back. The standalone app now refreshes this heartbeat on
// launch; Safari treats it as "installed" only while it is FRESH (within
// PWA_INSTALLED_TTL_MS). Once the user uninstalls they can no longer refresh it,
// so it goes stale and the hint returns. A legacy boolean `true` (pre-heartbeat)
// is treated as NOT fresh so already-affected users recover immediately.
export const PWA_INSTALLED_FLEX_KEY = 'pwa_installed';

// How long after the last standalone launch we still consider the PWA installed
// when read from Safari. Long enough not to nag a user who has the app but has
// not opened it in a while; short enough that an uninstall surfaces the hint
// again within ~2 weeks (iOS gives us no faster signal).
export const PWA_INSTALLED_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// Throttle: the standalone app only re-writes the heartbeat to the backend when
// the stored value is older than this, so a session doesn't POST user.flex on
// every mount/navigation.
export const PWA_INSTALLED_HEARTBEAT_MS = 12 * 60 * 60 * 1000; // 12 hours

/** True when the backend "installed" heartbeat is a timestamp within the TTL —
 *  i.e. the user launched the installed PWA recently. A legacy boolean `true`
 *  (written by the pre-heartbeat code) is NOT considered fresh, so a user who
 *  has since uninstalled sees the install hint again. */
export function isPwaInstalledRecently(flexValue: unknown): boolean {
    return typeof flexValue === 'number' && Date.now() - flexValue < PWA_INSTALLED_TTL_MS;
}

/** True when the standalone app should refresh the backend heartbeat now: the
 *  stored value is missing, a legacy non-number, or older than the throttle
 *  window. Keeps backend writes to ~once per session. */
export function shouldRefreshPwaInstalledHeartbeat(flexValue: unknown): boolean {
    return typeof flexValue !== 'number' || Date.now() - flexValue >= PWA_INSTALLED_HEARTBEAT_MS;
}

export function getInstallHintDismissedKey(): string {
    return INSTALL_HINT_DISMISSED_KEY;
}

export function getSessionCountKey(): string {
    return SESSION_COUNT_KEY;
}

// Session-scoped dismissal for the manual install hint(s). Stored in
// sessionStorage (NOT localStorage) on purpose: it sits between the old
// permanent-localStorage behaviour (the hint NEVER returned after one
// dismissal — the bug) and pure per-mount React state (the hint returned on
// EVERY reload / new tab / component remount, even mid-session — reviewer
// findings #1/#5). With sessionStorage a dismissal survives reloads and
// remounts within the same browsing session, but a fresh session (new tab
// group / after the browser is fully closed, or post-uninstall) shows the
// nudge again — which is exactly the recovery behaviour we want.
const INSTALL_HINT_SESSION_DISMISSED_KEY = 'bolmate-pwa-install-dismissed-session';

/** True when the user dismissed the manual install hint earlier in this
 *  browsing session. Safe in private mode / when storage is blocked. */
export function isManualInstallHintDismissed(): boolean {
    if (typeof sessionStorage === 'undefined') return false;
    try {
        return sessionStorage.getItem(INSTALL_HINT_SESSION_DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
}

/** Persist a session-scoped dismissal of the manual install hint. */
export function dismissManualInstallHint(): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(INSTALL_HINT_SESSION_DISMISSED_KEY, '1');
    } catch {
        /* storage disabled (private mode / quota) — fall back to per-mount state */
    }
}

// The Android install-guide banner is a different case from the hints above: it
// is the ONLY route to installing when the browser withholds its prompt, so
// hiding it for good is wrong — but so is showing it again on the next reload
// after the user just closed it. It snoozes instead: dismissed now, back after
// a day. localStorage (not session) so closing the tab does not undo the choice.
const INSTALL_HINT_SNOOZE_KEY = 'bolmate-pwa-install-hint-snoozed';
const INSTALL_HINT_SNOOZE_MS = 24 * 60 * 60 * 1000;

/** True while a snoozed install hint is still within its quiet period. A value
 *  that cannot be dated (legacy, tampered) counts as expired and is cleared. */
export function isInstallHintSnoozed(): boolean {
    try {
        if (typeof localStorage === 'undefined') return false;
        const raw = localStorage.getItem(INSTALL_HINT_SNOOZE_KEY);
        if (!raw) return false;
        const stamp = Number(raw);
        if (!Number.isFinite(stamp) || stamp <= 0 || Date.now() - stamp >= INSTALL_HINT_SNOOZE_MS) {
            localStorage.removeItem(INSTALL_HINT_SNOOZE_KEY);
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

/** Hide the install hint for the snooze period. */
export function snoozeInstallHint(): void {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(INSTALL_HINT_SNOOZE_KEY, String(Date.now()));
    } catch {
        /* storage disabled (private mode / quota) — falls back to per-mount state */
    }
}

/** True when the app is running as an installed PWA (standalone display). */
export function isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    const mql = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
    if (mql && mql.matches) return true;
    // iOS Safari — navigator.standalone is not in lib.dom typings.
    const iosStandalone = (window.navigator as unknown as {standalone?: boolean}).standalone;
    return iosStandalone === true;
}

// Persisted "installed" flag. A PWA only reports display-mode:standalone INSIDE
// its own window — back in the regular browser tab isStandalone() is false, and
// an already-installed app no longer fires beforeinstallprompt, so the browser
// tab has no other way to tell it is installed. Without this it keeps showing
// install banners (including the misleading "your browser can't install"
// Android hint) after the user already installed. Set on `appinstalled`,
// cleared when beforeinstallprompt fires again (which only happens while NOT
// installed, e.g. after an uninstall).
const PWA_INSTALLED_KEY = 'bolmate-pwa-installed';

// The flag is a TIMESTAMP, not a boolean, and it expires. `beforeinstallprompt`
// clears it the moment the browser says the app is installable again — but that
// event does not exist everywhere (no iOS Safari at all), so without an expiry
// an uninstall could leave the flag set forever and every install banner
// suppressed with it. Every standalone launch rewrites it (markPwaInstalledLocally),
// so a genuinely installed app that gets opened keeps the flag fresh; a device
// that stops launching the app falls back to showing the install banners a day
// later.
const PWA_INSTALLED_FRESH_MS = 24 * 60 * 60 * 1000;

/** True where the flag is not evidence of anything, because the platform can be
 *  ASKED instead: Chromium on Android answers `getInstalledRelatedApps()` from
 *  the browser tab (it needs the self-referencing `related_applications` entry
 *  in the manifest — see pwaInstallState). The flag is a guess that survives an
 *  uninstall and is only cleared by `beforeinstallprompt`, an event Chrome
 *  withholds in desktop-site mode and whenever the site is not currently
 *  installable — which left devices claiming "installed" with nothing
 *  installed. Deliberately NOT `hasRelatedAppsApi()` alone: the method also
 *  exists on desktop Chrome, where it returns [] for a perfectly installed
 *  desktop PWA, so trusting it there would resurrect the install banners for
 *  everyone who installed on a laptop. iOS has no API at all and keeps the flag. */
function usesRelatedAppsAuthority(): boolean {
    if (typeof navigator === 'undefined') return false;
    const hasApi = typeof (navigator as {getInstalledRelatedApps?: unknown}).getInstalledRelatedApps === 'function';
    return hasApi && isAndroid();
}

function setPwaInstalledFlag(value: boolean): void {
    try {
        if (typeof localStorage === 'undefined') return;
        if (value) localStorage.setItem(PWA_INSTALLED_KEY, String(Date.now()));
        else localStorage.removeItem(PWA_INSTALLED_KEY);
    } catch (_e) {
        /* private browsing */
    }
}

/** Reads the persisted flag, honouring its expiry. Legacy values ('1', written
 *  before the flag carried a timestamp) count as EXPIRED: they cannot be dated,
 *  and a stale one is exactly the state that hides the install banners forever
 *  after an uninstall. An installed app re-stamps the flag on its next launch. */
function hasFreshInstalledFlag(): boolean {
    try {
        if (typeof localStorage === 'undefined') return false;
        const raw = localStorage.getItem(PWA_INSTALLED_KEY);
        if (!raw) return false;
        // Chromium Android: the flag is never consulted, and a leftover one is
        // dropped on first read — that alone un-sticks a device that came out of
        // an install/uninstall cycle believing the app was still there.
        if (usesRelatedAppsAuthority()) {
            localStorage.removeItem(PWA_INSTALLED_KEY);
            return false;
        }
        const stamp = Number(raw);
        if (!Number.isFinite(stamp) || stamp <= 1) {
            localStorage.removeItem(PWA_INSTALLED_KEY);
            return false;
        }
        if (Date.now() - stamp >= PWA_INSTALLED_FRESH_MS) {
            localStorage.removeItem(PWA_INSTALLED_KEY);
            return false;
        }
        return true;
    } catch (_e) {
        return false;
    }
}

/** Persist the installed flag from the STANDALONE app itself. Covers installs
 *  that predate the `appinstalled` listener or that never fired it (e.g. some
 *  browser-menu installs): every standalone launch self-heals the browser
 *  tab's install detection. No-op on Chromium Android, which answers the
 *  question directly (usesRelatedAppsAuthority) and needs no guess. */
export function markPwaInstalledLocally(): void {
    if (usesRelatedAppsAuthority()) return;
    setPwaInstalledFlag(true);
}

/** True when the PWA is installed for this origin — either we are in the
 *  standalone window, or a previous `appinstalled` persisted the flag so the
 *  regular browser tab knows too. Used to suppress every install banner. */
export function isPWAInstalled(): boolean {
    if (isStandalone()) return true;
    return hasFreshInstalledFlag();
}

/** True when the current UA is iOS Safari (iPhone/iPad/iPod). */
export function isIOS(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as {MSStream?: unknown}).MSStream;
}

/** True when on iOS but NOT in Safari (e.g. Chrome, Firefox, Edge on iOS). */
export function isIOSNonSafari(): boolean {
    if (!isIOS()) return false;
    const ua = navigator.userAgent;
    return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

/** True when the current UA is Safari on macOS (not Chrome, not Edge, not iOS). */
export function isMacSafari(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const isMac = /Macintosh/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg\/|OPR\//.test(ua);
    return isMac && isSafari && !isIOS();
}

/** True when the current UA is Firefox (desktop or mobile). */
export function isFirefox(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Firefox\//.test(navigator.userAgent);
}

/** True when the current UA is Samsung Internet. Chromium-based, but
 *  `beforeinstallprompt` fires inconsistently across versions / install
 *  histories, so the UI falls back to a manual-install hint when the
 *  native prompt event never arrives. */
export function isSamsungBrowser(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /SamsungBrowser/.test(navigator.userAgent);
}

/** True when the current UA is Android (Chrome, Samsung Internet, Firefox Android, etc.). */
export function isAndroid(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Android/.test(navigator.userAgent);
}

/**
 * True on a touch-first device (phone / tablet), decided from the HARDWARE
 * instead of the user-agent string.
 *
 * "Wersja na komputer" / "Request desktop site" (Chrome on Android) and
 * "Request Desktop Website" (Safari on iOS) rewrite navigator.userAgent to a
 * desktop one — Android becomes "X11; Linux x86_64", iOS becomes
 * "Macintosh". isAndroid() / isIOS() then report false and the mobile push
 * rule (subscribe only from the installed app) silently stops applying in a
 * mobile browser tab. These signals come from the device and survive that
 * toggle: a real touchscreen, plus a primary pointer that is coarse or cannot
 * hover (no mouse, no trackpad).
 *
 * A touchscreen laptop keeps its trackpad and so reports 'pointer: fine' +
 * 'hover: hover' — it is NOT touch-first. iPhone/iPad in desktop mode report
 * a Mac platform, and no Mac has a touchscreen (maxTouchPoints is 0 there),
 * so for them the touch count alone settles it.
 */
export function isTouchFirstDevice(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const touchPoints = navigator.maxTouchPoints ?? 0;
    if (touchPoints < 1) return false;
    const platform = (navigator as unknown as {platform?: string}).platform ?? '';
    if (/Mac/i.test(platform) || /Macintosh/.test(navigator.userAgent)) return touchPoints > 1;
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches;
}

/** True on phones/tablets. The user-agent checks answer it for an untouched
 *  UA; isTouchFirstDevice() covers the browser's desktop-site toggle, which
 *  makes that UA lie. Every push gate uses THIS, never isIOS/isAndroid. */
export function isMobileDevice(): boolean {
    return isIOS() || isAndroid() || isTouchFirstDevice();
}

/**
 * True when the browser exposes the APIs required for a PWA experience
 * (service worker registration + push + notification). Pre-PWA browsers
 * (IE, very old Android WebView, niche) fail this check.
 */
export function isPWASupported(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

interface UserAgentDataShape {
    platform?: string;
    getHighEntropyValues?: (hints: Array<string>) => Promise<{
        platform?: string;
        platformVersion?: string;
        model?: string;
        architecture?: string;
    }>;
    brands?: Array<{brand: string; version: string}>;
}

function detectBrowser(): string {
    const ua = navigator.userAgent;
    if (/Edg\//.test(ua)) return 'Edge';
    if (/OPR\/|Opera/.test(ua)) return 'Opera';
    if (/SamsungBrowser/.test(ua)) return 'Samsung Internet';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/CriOS\//.test(ua)) return 'Chrome';
    if (/Chrome\//.test(ua)) return 'Chrome';
    if (/Safari\//.test(ua) && /Apple/.test(navigator.vendor || '')) return 'Safari';
    return '';
}

function detectOS(): string {
    const ua = navigator.userAgent;
    if (/Windows/.test(ua)) {
        const match = ua.match(/Windows NT (\d+\.\d+)/);
        if (match) {
            const nt = parseFloat(match[1]);
            if (nt >= 10.0) return 'Windows 11/10';
        }
        return 'Windows';
    }
    if (/Android/.test(ua)) return 'Android';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Mac OS X/.test(ua)) return 'macOS';
    if (/Linux/.test(ua)) return 'Linux';
    if (/CrOS/.test(ua)) return 'ChromeOS';
    return '';
}

function detectDeviceType(): string {
    const ua = navigator.userAgent;
    if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return i18n.t('push.deviceTablet', 'Tablet');
    if (/iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile/.test(ua))
        return i18n.t('push.devicePhone', 'Mobile device');
    return i18n.t('push.deviceComputer', 'Computer');
}

/**
 * Android device model via User-Agent Client Hints (e.g. "Pixel 7", "SM-G991B").
 * Modern Chrome's `navigator.userAgent` no longer carries the model (UA reduction
 * reports the literal "K"), so the model is only available from the high-entropy
 * `model` hint. Returns '' when unavailable — an older browser, a privacy setting,
 * or a non-Chromium Android browser without `userAgentData`.
 */
async function getAndroidModel(): Promise<string> {
    const uaData = (navigator as unknown as {userAgentData?: UserAgentDataShape}).userAgentData;
    if (!uaData?.getHighEntropyValues) return '';
    try {
        const hints = await uaData.getHighEntropyValues(['model']);
        return (hints.model || '').trim();
    } catch (_err) {
        return '';
    }
}

/**
 * 'iPhone' / 'iPad' for iOS/iPadOS devices, else ''. Apple exposes no device
 * model to the web, so the label distinguishes only the form factor. Also catches
 * iPadOS 13+ Safari, which reports a desktop "Macintosh" UA but is the only "Mac"
 * with a multi-touch screen (real Macs report maxTouchPoints === 0).
 */
function getIOSDeviceKind(): string {
    if (typeof navigator === 'undefined') return '';
    const ua = navigator.userAgent;
    if (/iPhone|iPod/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'iPad';
    return '';
}

/**
 * Build a human-readable device label for the device_token.platform column.
 * Mobile registrations only ever come from the installed app (the mobile
 * install gate blocks browser-context registrations), so phones/tablets get a
 * brand label — identical in every UI language — that makes each row in the
 * device list distinguishable: the Android model via Client Hints
 * ("Pixel 7, Bolmate app"), and the iOS form factor ("iPhone, Bolmate app" /
 * "iPad, Bolmate app") since Apple exposes no model to the web. Android falls
 * back to "Android, Bolmate app" when the model hint is unavailable. Desktop
 * keeps the descriptive format "Computer – Windows 11, Chrome".
 */
export async function getDeviceLabel(): Promise<string> {
    if (typeof navigator === 'undefined') return '';
    if (isAndroid()) {
        const model = await getAndroidModel();
        return model ? `${model}, Bolmate app` : 'Android, Bolmate app';
    }
    const iosKind = getIOSDeviceKind();
    if (iosKind) return `${iosKind}, Bolmate app`;

    let os = '';
    const uaData = (navigator as unknown as {userAgentData?: UserAgentDataShape}).userAgentData;
    if (uaData?.getHighEntropyValues) {
        try {
            const hints = await uaData.getHighEntropyValues(['platform', 'platformVersion']);
            if (hints.platform) {
                const majorVersion = (hints.platformVersion || '').split('.')[0];
                if (hints.platform === 'Windows' && parseInt(majorVersion) >= 13) {
                    os = 'Windows 11';
                } else if (hints.platform === 'Windows') {
                    os = parseInt(majorVersion) >= 10 ? 'Windows 10' : 'Windows';
                } else if (hints.platform === 'macOS') {
                    os = majorVersion ? `macOS ${majorVersion}` : 'macOS';
                } else {
                    os = hints.platform;
                }
            }
        } catch (_err) {
            // fall through
        }
    }
    if (!os) os = detectOS();

    const deviceType = detectDeviceType();
    const browser = detectBrowser();
    const standalone = isStandalone();

    const parts: Array<string> = [];
    if (os) parts.push(os);
    if (browser) parts.push(browser + (standalone ? ` (${i18n.t('push.installedApp', 'geïnstalleerde app')})` : ''));

    const detail = parts.join(', ');
    return detail ? `${deviceType} – ${detail}` : deviceType;
}

/**
 * Register the Workbox-built service worker and wire up the update lifecycle.
 *
 * Runs in all modes. In dev, InjectManifest excludes all assets from precache
 * (see webpack.config.js) so push works end-to-end without caching stale bundles.
 *
 * Returns the ServiceWorkerRegistration or null if SW is unsupported / skipped.
 */
// Vite dev serves the SW as an ES module (vite-plugin-pwa injectManifest
// devOptions) — registering it as a classic script fails with "script
// evaluation failed" on the first `import`. Production builds ship a
// self-contained script, registered classic as before.
const SW_REGISTER_OPTIONS: RegistrationOptions = {
    scope: '/',
    updateViaCache: 'none',
    ...(import.meta.env.DEV ? {type: 'module' as WorkerType} : {})
};

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === 'undefined') return null;
    if (!('serviceWorker' in navigator)) return null;

    try {
        // updateViaCache: 'none' forces the browser to fetch sw.js from the
        // NETWORK (bypassing the HTTP cache) on every update check. With the
        // default ('imports') the top-level sw.js is served from the HTTP cache,
        // so registration.update() never detects a new deploy while the PWA stays
        // open — the user had to fully close and relaunch to pick up changes
        // (the reported Android behaviour). With 'none', the periodic +
        // visibilitychange update() polling below actually sees the new sw.js,
        // workbox fires 'waiting', and the "update available" banner appears.
        workbox = new Workbox('/sw.js', SW_REGISTER_OPTIONS);

        workbox.addEventListener('waiting', () => {
            // Notify-and-apply: a new version is waiting. Surface the "update
            // available" banner rather than reloading under the user; they apply
            // it with one click. Dev skips the banner (it would nag on every HMR
            // rebuild) and activates the waiting worker immediately instead —
            // without this the old worker keeps serving its stale precache after
            // a rebuild until every tab closes or the SW is unregistered.
            if (process.env.NODE_ENV === 'development') {
                workbox?.messageSkipWaiting();
                return;
            }
            applyServiceWorkerUpdate();
        });

        workbox.addEventListener('controlling', () => {
            // Ignore 'controlling' from the very first install (no previous
            // controller) and from OTHER tabs' updates — only OUR activation
            // (applyServiceWorkerUpdate) should reload this tab.
            if (!swUpdateActivating) return;
            // The user applied the update; reload to surface the new bundle
            // (deferred only while mid-edit so unsaved input is never lost).
            scheduleReloadWhenSafe();
        });

        const reg = await workbox.register();
        registration = reg;

        // A navigation-triggered update check can put the new worker in
        // `waiting` before register() attaches its listeners — the 'waiting'
        // event above then never fires for it. Dev activates such a worker
        // directly, or the stale one keeps controlling until the tab closes.
        if (process.env.NODE_ENV === 'development' && reg?.waiting) {
            workbox.messageSkipWaiting();
        }

        if (registration && process.env.NODE_ENV !== 'development') {
            // Periodic + visibilitychange update polling is a PROD-only path.
            // In dev, webpack-dev-server rebuilds the SW on every frontend
            // change (InjectManifest regenerates the precache list), and
            // each registration.update() pulls the new sw.js which then
            // installs + skipWaiting + claims clients. The 'waiting' update
            // banner and 'controlling' reload are both gated to non-dev, so dev
            // is not reloaded on every rebuild — but the SW is still constantly
            // re-evaluated, spamming the console with Workbox logs and
            // re-running push-handler setup. We rely on a full hard refresh
            // (Ctrl+Shift+R) in dev for a deliberate SW refresh.
            // iOS/WebKit rejects registration.update() with
            // "InvalidStateError: newestWorker is null" whenever the
            // registration currently has NO installing/waiting/active worker — a
            // state iOS PWAs genuinely sit in when the SW failed to install.
            // Guard the call (skip when there is no worker) and swallow any
            // rejection so the poll never raises an unhandled promise rejection.
            const safeUpdate = () => {
                if (!registration) return;
                if (!registration.installing && !registration.waiting && !registration.active) return;
                registration.update().catch(() => undefined);
            };
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') safeUpdate();
            });
            setInterval(safeUpdate, 60_000);
        }

        // workbox `register()` resolves to `ServiceWorkerRegistration | undefined`;
        // normalise `undefined` to `null` to match this function's return type.
        return registration ?? null;
    } catch (err) {
        console.warn('[pwa] Service worker registration failed', err);
        return null;
    }
}

/**
 * Apply the waiting SW — the user clicked "update now" on the update-available
 * banner — and reload to surface the new bundle. Never shows a dialog; defers
 * the reload only while the user is mid-edit so unsaved input is never lost.
 */
export function applyServiceWorkerUpdate(): void {
    // Mark the activation as ours so the 'controlling' listener reloads (it
    // ignores first-install / other-tab 'controlling').
    swUpdateActivating = true;
    if (!workbox) {
        scheduleReloadWhenSafe();
        return;
    }
    // No waiting worker to activate (another tab already applied it, or it
    // already activated): reload to surface whatever is now controlling.
    if (registration && !registration.waiting) {
        scheduleReloadWhenSafe();
        return;
    }
    // Activate the waiting worker; sw.ts's SKIP_WAITING handler calls
    // skipWaiting(), which fires 'controlling' and drives the reload above.
    void workbox.messageSkipWaiting();
}

/** Reload to load the freshly activated bundle, but never while the user is
 *  editing: defer until they stop typing or the tab is backgrounded, so an
 *  auto-update can never discard unsaved input. */
function scheduleReloadWhenSafe(): void {
    if (!isUserEditingInput()) {
        reloadForUpdate();
        return;
    }
    if (deferredReloadArmed) return;
    deferredReloadArmed = true;
    const finish = () => {
        deferredReloadArmed = false;
        window.clearInterval(poll);
        document.removeEventListener('visibilitychange', onVisibility);
        reloadForUpdate();
    };
    const onVisibility = () => {
        // Backgrounding the tab is a safe moment — reload now so the user
        // returns to the new version with no visible interruption.
        if (document.visibilityState === 'hidden') finish();
    };
    const poll = window.setInterval(() => {
        if (!isUserEditingInput()) finish();
    }, 5000);
    document.addEventListener('visibilitychange', onVisibility);
}

function reloadForUpdate(): void {
    // Flag the reload so the next load can show the "app updated" toast.
    try {
        sessionStorage.setItem(PWA_JUST_UPDATED_KEY, '1');
    } catch {
        /* storage disabled (private mode) — skip the post-reload toast */
    }
    window.location.reload();
}

/**
 * Resolve a ServiceWorkerRegistration that has an ACTIVE worker, bounded by
 * `timeoutMs` (rejects with `sw-not-ready` on timeout / unsupported).
 *
 * Robust against iOS standalone PWAs, where `navigator.serviceWorker.ready` is
 * known to hang even though a usable registration exists. Strategy, each step
 * returning early once an active worker is in hand:
 *   1. The workbox registration (or getRegistration) is already active → return
 *      it now. This is the iOS fix: `.ready` may never settle, but the worker is
 *      in fact active and reachable via getRegistration().
 *   2. No registration yet (toggle tapped before registerServiceWorker() ran, or
 *      a fresh home-screen launch) → register `/sw.js` now (idempotent for the
 *      same script + scope).
 *   3. Wait for the installing/waiting worker to reach `activated` via
 *      `statechange`.
 *   4. Last resort: the spec `ready` promise.
 * Relying on `.ready` alone (the previous behaviour) surfaced a spurious
 * "service worker is not ready" error to iOS users whose SW was fine.
 */
export function awaitActiveServiceWorker(timeoutMs = 15000): Promise<ServiceWorkerRegistration> {
    let timer: number | undefined;
    const timeout = new Promise<never>((_, reject) => {
        timer = window.setTimeout(() => reject(new Error('sw-not-ready')), timeoutMs);
    });

    const resolveActive = async (): Promise<ServiceWorkerRegistration> => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
            throw new Error('sw-not-ready');
        }
        // 1. Already-active registration — instant on iOS where `.ready` hangs.
        if (registration?.active) return registration;
        let reg = (await navigator.serviceWorker.getRegistration('/')) ?? registration;
        if (reg?.active) return reg;
        // 2. (Re)register when there is no registration at all, OR a registration
        //    exists but has NO worker (iOS can leave an orphaned registration
        //    where installing/waiting/active are all null — the "newestWorker is
        //    null" state). register() is idempotent for the same script+scope and
        //    kicks a fresh install on an empty registration, self-healing it.
        if (!reg || !(reg.installing || reg.waiting || reg.active)) {
            reg = await navigator.serviceWorker.register('/sw.js', SW_REGISTER_OPTIONS);
        }
        if (reg.active) return reg;
        // 3. Wait for the installing/waiting worker to activate.
        const worker = reg.installing ?? reg.waiting;
        if (worker) {
            await new Promise<void>(resolve => {
                const onState = () => {
                    if (worker.state === 'activated') {
                        worker.removeEventListener('statechange', onState);
                        resolve();
                    }
                };
                worker.addEventListener('statechange', onState);
                // It may have activated between the checks above and this listener.
                if (worker.state === 'activated') {
                    worker.removeEventListener('statechange', onState);
                    resolve();
                }
            });
            if (reg.active) return reg;
        }
        // 4. Last resort — the spec ready promise.
        return navigator.serviceWorker.ready;
    };

    return Promise.race([resolveActive(), timeout]).finally(() => {
        if (timer !== undefined) window.clearTimeout(timer);
    });
}

/**
 * Capture the beforeinstallprompt event so we can trigger it on demand.
 * Returns an unregister function.
 */
export function setupInstallPrompt(): () => void {
    if (typeof window === 'undefined') return () => undefined;

    // Adopt an event the early listener in index.html already caught. This runs
    // from a React mount effect, by which time Chrome has often already fired
    // (and a missed beforeinstallprompt never comes back on its own) — that is
    // what left the install banners permanently hidden.
    const parked = (window as unknown as {__bolmateInstallPrompt?: BeforeInstallPromptEvent | null})
        .__bolmateInstallPrompt;
    if (parked && !deferredInstallPrompt) {
        deferredInstallPrompt = parked;
        setPwaInstalledFlag(false);
        // Announce it, exactly as the live handler below does. The banners
        // evaluate in their own mount effect, which React runs BEFORE this
        // parent effect — so by the time the prompt is adopted they have already
        // decided "no prompt, nothing to show" and, without this event, nothing
        // ever tells them otherwise. Chrome fires early precisely in the case
        // that matters: right after an uninstall, when the origin is already
        // known to be installable and the event beats React to the page.
        dispatchWindowEvent(PWA_INSTALL_AVAILABLE_EVENT);
    }

    const onBeforeInstallPrompt = (event: Event) => {
        event.preventDefault();
        deferredInstallPrompt = event as BeforeInstallPromptEvent;
        // Installable ⇒ not installed. Clear any stale installed flag (e.g. the
        // user uninstalled — Chrome re-fires beforeinstallprompt when eligible)
        // so the install banners can legitimately show again.
        setPwaInstalledFlag(false);
        dispatchWindowEvent(PWA_INSTALL_AVAILABLE_EVENT);
    };

    const onAppInstalled = () => {
        deferredInstallPrompt = null;
        (window as unknown as {__bolmateInstallPrompt?: BeforeInstallPromptEvent | null}).__bolmateInstallPrompt = null;
        // Deliberately NOT persisting the installed flag here. Chrome fires
        // `appinstalled` for the "create shortcut" branch of its add-to-home-screen
        // dialog too — and a shortcut is a bookmark, not an app: it opens in a tab
        // and receives no push. Trusting this event made the app believe it was
        // installed after a shortcut, hiding every install banner from a user who
        // never got the app. The ONLY proof of a real install is a launch in
        // standalone display mode, which is where markPwaInstalledLocally() writes
        // the flag (app.tsx). Banners still hide for the rest of THIS page view via
        // the event below — session state, nothing persisted.
        dispatchWindowEvent(PWA_INSTALL_AVAILABLE_EVENT);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.removeEventListener('appinstalled', onAppInstalled);
    };
}

export function hasInstallPrompt(): boolean {
    return deferredInstallPrompt !== null;
}

/**
 * Trigger the native install prompt. Returns 'unavailable' when the browser
 * never fired beforeinstallprompt (e.g. iOS Safari — use the iOS hint banner
 * instead).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!deferredInstallPrompt) return 'unavailable';
    const prompt = deferredInstallPrompt;
    // The event is single-use — drop both this reference and the one parked by
    // the early listener in index.html.
    deferredInstallPrompt = null;
    (window as unknown as {__bolmateInstallPrompt?: BeforeInstallPromptEvent | null}).__bolmateInstallPrompt = null;
    try {
        await prompt.prompt();
        const choice = await prompt.userChoice;
        return choice.outcome;
    } catch (_err) {
        return 'dismissed';
    } finally {
        // The prompt is spent either way. Anything that renders off
        // hasInstallPrompt() — the banner itself, and the guide step that
        // frames it — has to re-evaluate, or the step outlives its banner.
        dispatchWindowEvent(PWA_INSTALL_AVAILABLE_EVENT);
    }
}

export function useOnlineStatus(): boolean {
    const [online, setOnline] = useState(navigator.onLine);
    useEffect(() => {
        const goOnline = () => setOnline(true);
        const goOffline = () => setOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);
    return online;
}
